/**
 * theme-density.ts — pure DOM + localStorage helpers for theme/density prefs.
 *
 * applyTheme + applyDensity MUTATE `document.documentElement`; readInitialPrefs is pure-read.
 * Storage keys (`linkhr:theme`, `linkhr:density`) are shared with the FOWT
 * inline script in `fowt-script.ts` — any rename must stay in lockstep.
 *
 * Safari private-mode throws `SecurityError` on `localStorage.setItem`; every
 * write is wrapped in try/catch so applyTheme/applyDensity never surface the
 * error into React (DOM mutation still succeeds; persistence silently no-ops).
 */
export type Theme = "light" | "dark";
export type Density = "comfortable" | "compact";
export type Glass = "on" | "off";

export const STORAGE_KEYS = {
  theme: "linkhr:theme",
  density: "linkhr:density",
  glass: "linkhr:glass",
} as const;

const THEMES: readonly Theme[] = ["light", "dark"];
const DENSITIES: readonly Density[] = ["comfortable", "compact"];
const GLASS_STATES: readonly Glass[] = ["on", "off"];

function isTheme(v: unknown): v is Theme {
  return typeof v === "string" && (THEMES as readonly string[]).includes(v);
}

function isDensity(v: unknown): v is Density {
  return typeof v === "string" && (DENSITIES as readonly string[]).includes(v);
}

function isGlass(v: unknown): v is Glass {
  return typeof v === "string" && (GLASS_STATES as readonly string[]).includes(v);
}

/**
 * Applies theme to the document:
 *   - `<html data-theme="light|dark">` — scopes semantic-color overrides
 *   - `html.style.colorScheme` — tells the UA to render native form controls,
 *     scrollbars, and `<input type="date">` popups in matching theme chrome
 *     (Pitfall 6 — without this, native surfaces show light-theme even under
 *     `[data-theme="dark"]`)
 *   - persists to localStorage (wrapped; Safari private-mode safe)
 */
export function applyTheme(theme: Theme): void {
  const html = document.documentElement;
  html.setAttribute("data-theme", theme);
  html.style.colorScheme = theme;
  // Keep the browser-chrome color on the effective theme (manual toggles win
  // over the OS here, so a media-paired meta would disagree). Brand values,
  // not UI tokens — the kit's theme colors.
  let meta = document.querySelector('meta[name="theme-color"]');
  if (meta === null) {
    meta = document.createElement("meta");
    meta.setAttribute("name", "theme-color");
    document.head.appendChild(meta);
  }

  meta.setAttribute("content", theme === "dark" ? "#0E1621" : "#5AB6CB");
  try {
    localStorage.setItem(STORAGE_KEYS.theme, theme);
  } catch {
    /* Safari private mode / quota exceeded — DOM already updated; skip persistence. */
  }
}

/** Applies density to the document (`<html data-density="comfortable|compact">`) + persists. */
export function applyDensity(density: Density): void {
  document.documentElement.setAttribute("data-density", density);
  try {
    localStorage.setItem(STORAGE_KEYS.density, density);
  } catch {
    /* Safari private mode — skip persistence. */
  }
}

/** Applies the glass theme to the document (`<html data-glass="on|off">`) + persists.
 *  Demo-only: gates `.linkhr-glass` surface treatment via `tokens/glass.css`. */
export function applyGlass(glass: Glass): void {
  document.documentElement.setAttribute("data-glass", glass);
  try {
    localStorage.setItem(STORAGE_KEYS.glass, glass);
  } catch {
    /* Safari private mode — skip persistence. */
  }
}

/**
 * Reads initial prefs from localStorage with graceful fallback:
 *   1. Stored theme (if valid) → use it
 *   2. Else if `prefers-color-scheme: dark` media query matches → dark
 *   3. Else → light
 *   4. Density: stored (if valid), else comfortable
 *
 * Used by Storybook decorator + reconcileWithBackend to compare against server prefs.
 * The production FOWT inline script in `fowt-script.ts` implements the same logic
 * synchronously in vanilla JS BEFORE React boots (no ES imports allowed there).
 */
export function readInitialPrefs(): { theme: Theme; density: Density; glass: Glass } {
  let theme: Theme = "light";
  let density: Density = "comfortable";
  let glass: Glass = "off";
  try {
    const t = localStorage.getItem(STORAGE_KEYS.theme);
    const d = localStorage.getItem(STORAGE_KEYS.density);
    const g = localStorage.getItem(STORAGE_KEYS.glass);
    if (isTheme(t)) {
      theme = t;
    } else if (
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-color-scheme: dark)").matches
    ) {
      theme = "dark";
    }
    if (isDensity(d)) density = d;
    if (isGlass(g)) glass = g;
  } catch {
    /* localStorage blocked → fall through to defaults. */
  }
  return { theme, density, glass };
}
